<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 600));

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "linear-gradient(180deg, #2a2a2a 0%, #111 100%)",
      fontFamily:
        props.block.fontFamily || "'IBM Plex Mono', 'Roboto Mono', monospace",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 0.85}px`,
    height: `${base * 1.4}px`,
    fontSize: `${base}px`,
  };
});

function digitOffset(d: string): string {
  const n = parseInt(d, 10);
  if (Number.isNaN(n)) return "0%";
  // Strip is 1000% (10× wheel height) tall and contains 10 digit slots.
  // translateY % is relative to the element's OWN height, so 1 slot = 10%.
  return `${-n * 10}%`;
}
</script>

<template>
  <div class="cd-mech" :style="containerStyle">
    <div v-for="seg in segments" :key="seg.key" class="cd-mech__group">
      <div class="cd-mech__panel">
        <div
          v-for="(_, ci) in seg.value"
          :key="ci"
          class="cd-mech__wheel"
          :style="{
            width: cardSize.width,
            height: cardSize.height,
            fontSize: cardSize.fontSize,
          }"
        >
          <div class="cd-mech__shadow cd-mech__shadow--top" />
          <div class="cd-mech__shadow cd-mech__shadow--bot" />
          <div
            class="cd-mech__strip"
            :style="{ transform: `translateY(${digitOffset(seg.value[ci])})` }"
          >
            <span v-for="n in 10" :key="n">{{ n - 1 }}</span>
          </div>
        </div>
      </div>
      <div
        class="cd-mech__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || 'rgba(255, 255, 255, 0.55)',
        }"
      >
        {{ seg.label }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.cd-mech {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.cd-mech__group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.cd-mech__panel {
  display: flex;
  gap: 4px;
  padding: 8px 10px;
  background: linear-gradient(180deg, #1a1a1a, #050505);
  border-radius: 8px;
  box-shadow:
    inset 0 0 14px rgba(0, 0, 0, 0.85),
    0 4px 14px rgba(0, 0, 0, 0.55);
}
.cd-mech__wheel {
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, #3a3a3a 0%, #b0b0b0 50%, #3a3a3a 100%);
  border-radius: 4px;
  box-shadow:
    inset 0 6px 8px rgba(0, 0, 0, 0.5),
    inset 0 -6px 8px rgba(0, 0, 0, 0.5);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #1a1a1a;
}
.cd-mech__shadow {
  position: absolute;
  left: 0;
  right: 0;
  height: 32%;
  pointer-events: none;
  z-index: 2;
}
.cd-mech__shadow--top {
  top: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.6), transparent);
}
.cd-mech__shadow--bot {
  bottom: 0;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.6), transparent);
}
.cd-mech__strip {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  height: 1000%;
  transition: transform var(--cd-tick, 600ms) cubic-bezier(0.65, 0.05, 0.36, 1);
  will-change: transform;
}
.cd-mech__strip span {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 10%;
}
.cd-mech__label {
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-weight: 500;
}
</style>
